// @ts-nocheck
/**
 * Storage utilities for iOS Safari/Chrome compatibility
 * Provides fallback mechanisms when localStorage is unavailable
 */

import { deleteCookie } from 'cookies-next';

/**
 * Check if a storage type is available
 */
export const isStorageAvailable = (type: 'localStorage' | 'sessionStorage'): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    // QuotaExceededError or SecurityError (private browsing)
    return false;
  }
};

/**
 * Store token with fallback mechanism
 * Tries localStorage first, then sessionStorage
 */
export const storeToken = (token: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Primary: localStorage
  if (isStorageAvailable('localStorage')) {
    try {
      localStorage.setItem('token', token);
      // Verify it was saved (iOS needs this)
      if (localStorage.getItem('token') === token) {
        return true;
      }
    } catch (e) {
      console.warn('localStorage failed, trying sessionStorage:', e);
    }
  }

  // Fallback: sessionStorage
  if (isStorageAvailable('sessionStorage')) {
    try {
      sessionStorage.setItem('token', token);
      // Verify it was saved
      if (sessionStorage.getItem('token') === token) {
        return true;
      }
    } catch (e) {
      console.error('Both localStorage and sessionStorage failed:', e);
      return false;
    }
  }

  // Note: Cookie is set by backend, no need to set here
  // If both storages fail, cookie will still work
  return false;
};

/**
 * Retrieve token with fallback mechanism
 * Tries localStorage first, then sessionStorage
 * Note: Cookie is automatically sent by browser, no need to read it
 */
export const getToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Try localStorage first
  if (isStorageAvailable('localStorage')) {
    try {
      const token = localStorage.getItem('token');
      if (token) return token;
    } catch (e) {
      console.warn('localStorage read failed:', e);
    }
  }

  // Fallback to sessionStorage
  if (isStorageAvailable('sessionStorage')) {
    try {
      const token = sessionStorage.getItem('token');
      if (token) return token;
    } catch (e) {
      console.warn('sessionStorage read failed:', e);
    }
  }

  // Cookie is automatically sent by browser, no need to read it
  return null;
};

/**
 * Remove token from all storages
 */
export const removeToken = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem('token');
  } catch (e) {
    // Ignore errors
  }

  try {
    sessionStorage.removeItem('token');
  } catch (e) {
    // Ignore errors
  }

  // Clear cookie
  try {
    deleteCookie('token');
  } catch (e) {
    // Ignore errors
  }

  // Cookie is cleared by backend on logout
};

/**
 * Verify token is saved (with retry for iOS)
 * iOS Safari/Chrome sometimes needs a moment to persist localStorage
 */
export const verifyTokenSaved = async (token: string, maxRetries: number = 5): Promise<boolean> => {
  for (let i = 0; i < maxRetries; i++) {
    const savedToken = getToken();
    if (savedToken === token) {
      return true;
    }

    // Small delay before retry (iOS needs this)
    if (i < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  return false;
};

/**
 * Generic storage functions for other keys (impersonation, parent portal, etc.)
 */
const getStorageValue = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Try localStorage first
  if (isStorageAvailable('localStorage')) {
    try {
      const value = localStorage.getItem(key);
      if (value) return value;
    } catch (e) {
      console.warn(`localStorage read failed for ${key}:`, e);
    }
  }

  // Fallback to sessionStorage
  if (isStorageAvailable('sessionStorage')) {
    try {
      const value = sessionStorage.getItem(key);
      if (value) return value;
    } catch (e) {
      console.warn(`sessionStorage read failed for ${key}:`, e);
    }
  }

  return null;
};

const setStorageValue = (key: string, value: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Primary: localStorage
  if (isStorageAvailable('localStorage')) {
    try {
      localStorage.setItem(key, value);
      // Verify it was saved (iOS needs this)
      if (localStorage.getItem(key) === value) {
        return true;
      }
    } catch (e) {
      console.warn(`localStorage failed for ${key}, trying sessionStorage:`, e);
    }
  }

  // Fallback: sessionStorage
  if (isStorageAvailable('sessionStorage')) {
    try {
      sessionStorage.setItem(key, value);
      // Verify it was saved
      if (sessionStorage.getItem(key) === value) {
        return true;
      }
    } catch (e) {
      console.error(`Both storages failed for ${key}:`, e);
      return false;
    }
  }

  return false;
};

const removeStorageValue = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Ignore errors
  }

  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // Ignore errors
  }
};

// Parent Portal Token
export const getParentPortalToken = (): string | null => getStorageValue('parent_portal_token');
export const setParentPortalToken = (token: string): boolean => setStorageValue('parent_portal_token', token);
export const removeParentPortalToken = (): void => removeStorageValue('parent_portal_token');

// Impersonation Data (stored as JSON string)
export const getImpersonationData = (): unknown | null => {
  const data = getStorageValue('impersonating');
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch (e) {
    console.error('Failed to parse impersonation data:', e);
    return null;
  }
};
export const setImpersonationData = (data: unknown): boolean => {
  try {
    return setStorageValue('impersonating', JSON.stringify(data));
  } catch (e) {
    console.error('Failed to stringify impersonation data:', e);
    return false;
  }
};
export const removeImpersonationData = (): void => removeStorageValue('impersonating');

// Original Admin Token (for impersonation)
export const getOriginalAdminToken = (): string | null => getStorageValue('original_admin_token');
export const setOriginalAdminToken = (token: string): boolean => setStorageValue('original_admin_token', token);
export const removeOriginalAdminToken = (): void => removeStorageValue('original_admin_token');
